import { useState } from 'react';
import { useIntegrations } from '@/hooks/useIntegrations';
import { useStripeConnect } from '@/hooks/useStripeConnect';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CreditCard, Loader2, Trash2, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import type { StripeIntegration } from '@/contexts/IntegrationsContext';
import { useLocalStorage } from '@/hooks/useLocalStorage';

export default function IntegrationsSettings() {
  const { settings, removeIntegration } = useIntegrations();
  const [integrationToDelete, setIntegrationToDelete] = useState<StripeIntegration | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [stripeClientId, setStripeClientId] = useLocalStorage<string>('stripe_client_id', '');
  const [clientIdInput, setClientIdInput] = useState(stripeClientId);
  const [isEditingClientId, setIsEditingClientId] = useState(false);

  const stripeConnect = useStripeConnect({
    clientId: stripeClientId || import.meta.env.VITE_STRIPE_CLIENT_ID || '',
    redirectUri: `${window.location.origin}/settings/integrations/stripe/callback`,
  });

  const handleSaveClientId = () => {
    setStripeClientId(clientIdInput.trim());
    setIsEditingClientId(false);
  };

  const handleConnectStripe = () => {
    if (!stripeConnect.isConfigured) {
      setIsEditingClientId(true);
      return;
    }

    stripeConnect.initiateConnect();
  };

  const handleDeleteIntegration = async () => {
    if (!integrationToDelete) return;

    setIsDeleting(true);
    try {
      if (integrationToDelete.type === 'stripe') {
        await stripeConnect.disconnect(integrationToDelete);
      } else {
        // Generic removal for other integration types
        removeIntegration(integrationToDelete.id);
      }
      setIntegrationToDelete(null);
    } catch (error) {
      console.error('Failed to delete integration:', error);
      alert('Failed to delete integration. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const stripeIntegrations = settings.integrations.filter(
    (i): i is StripeIntegration => i.type === 'stripe'
  );

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Payments</h1>
        <p className="text-muted-foreground mt-2">
          Connect payment providers to accept payments in your projects
        </p>
      </div>

      <Separator />

        {/* Stripe Connect Section */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>Stripe</CardTitle>
                  <CardDescription>Accept payments in your projects</CardDescription>
                </div>
              </div>
              {stripeConnect.isConfigured && stripeIntegrations.length === 0 && (
                <Button onClick={handleConnectStripe} disabled={stripeConnect.isConnecting}>
                  {stripeConnect.isConnecting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    'Connect Stripe Account'
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!stripeConnect.isConfigured && (
              <div className="space-y-4">
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    To accept payments, you need to connect your Stripe account.
                  </AlertDescription>
                </Alert>

                <div className="bg-muted/50 p-4 rounded-lg space-y-4">
                  <div>
                    <h3 className="font-medium text-sm mb-2">Setup Instructions:</h3>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                      <li>
                        Go to your{' '}
                        <a
                          href="https://dashboard.stripe.com/settings/applications"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          Stripe Dashboard
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </li>
                      <li>Click "Create application" under Connect settings</li>
                      <li>Add this redirect URI to your application:</li>
                    </ol>
                    <code className="block mt-2 text-xs bg-background p-2 rounded border">
                      {window.location.origin}/settings/integrations/stripe/callback
                    </code>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="stripe-client-id">Paste your Stripe Client ID:</Label>
                    <div className="flex gap-2">
                      <Input
                        id="stripe-client-id"
                        type="text"
                        placeholder="ca_xxxxxxxxxxxxx"
                        value={clientIdInput}
                        onChange={(e) => setClientIdInput(e.target.value)}
                        className="font-mono text-sm"
                      />
                      <Button
                        onClick={handleSaveClientId}
                        disabled={!clientIdInput.trim() || clientIdInput.trim() === stripeClientId}
                      >
                        Save
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Your Client ID starts with "ca_" and can be found in your Stripe Connect settings.
                    </p>
                  </div>

                  {stripeClientId && (
                    <Button onClick={handleConnectStripe} className="w-full">
                      Connect Stripe Account
                    </Button>
                  )}
                </div>
              </div>
            )}

            {stripeConnect.error && (
              <Alert variant="destructive">
                <AlertDescription>{stripeConnect.error}</AlertDescription>
              </Alert>
            )}

            {stripeIntegrations.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium">Connected Accounts</p>
                {stripeIntegrations.map((integration) => (
                  <div
                    key={integration.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{integration.name}</p>
                        <Badge variant={integration.livemode ? 'default' : 'secondary'}>
                          {integration.livemode ? 'Live' : 'Test'}
                        </Badge>
                        {integration.enabled && (
                          <Badge variant="outline" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Active
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-4 text-sm text-muted-foreground">
                        <span>Account: {integration.stripeAccountId}</span>
                        {integration.metadata?.email && (
                          <span>Email: {integration.metadata.email}</span>
                        )}
                        {integration.metadata?.country && (
                          <span>Country: {integration.metadata.country}</span>
                        )}
                      </div>
                      {integration.scopes && integration.scopes.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Permissions: {integration.scopes.join(', ')}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIntegrationToDelete(integration)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {stripeConnect.isConfigured && (
                  <Button
                    variant="outline"
                    onClick={handleConnectStripe}
                    disabled={stripeConnect.isConnecting}
                  >
                    Connect Another Account
                  </Button>
                )}
              </div>
            )}

            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Once connected, enable the <span className="font-medium text-foreground">Stripe Payments</span> skill in{' '}
                <a href="/settings/ai" className="text-primary hover:underline">
                  Settings &gt; AI
                </a>{' '}
                to let the AI help you add payments to your projects.
              </p>
            </div>
          </CardContent>
        </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!integrationToDelete}
        onOpenChange={(open) => !open && setIntegrationToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Integration?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect "{integrationToDelete?.name}"? This will revoke
              access and remove the integration from all your projects. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteIntegration}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
